import { type FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useSearchParams } from "react-router";
import { PlatformSocialLoginButtons } from "../components/PlatformSocialLoginButtons";
import { usePlatformAuth } from "../context/platform-auth";

export function PlatformSignUpPage() {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const redirect = searchParams.get("redirect") || "/workspaces";
	const { t } = useTranslation();
	const { checkSession } = usePlatformAuth();
	const [name, setName] = useState("");
	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [tosAccepted, setTosAccepted] = useState(false);
	const [error, setError] = useState("");
	const [loading, setLoading] = useState(false);

	async function handleSubmit(e: FormEvent) {
		e.preventDefault();
		setError("");
		setLoading(true);
		try {
			const res = await fetch("/api/platform/auth/sign-up/email", {
				method: "POST",
				credentials: "include",
				headers: {
					"Content-Type": "application/json",
					Origin: window.location.origin,
				},
				body: JSON.stringify({ name, email, password }),
			});
			if (!res.ok) {
				const body = await res.json().catch(() => null);
				setError(
					(body as { message?: string } | null)?.message ??
						t("auth.signUp.failedWithStatus", { status: res.status }),
				);
				return;
			}
			await checkSession();
			navigate(redirect);
		} catch {
			setError(t("auth.signUp.networkError"));
		} finally {
			setLoading(false);
		}
	}

	const signInLink =
		redirect !== "/workspaces"
			? `/sign-in?redirect=${encodeURIComponent(redirect)}`
			: "/sign-in";

	return (
		<>
			<h1 className="text-2xl font-bold text-gray-900 mb-4">
				{t("auth.signUp.heading")}
			</h1>
			{error && <p className="text-red-600 text-sm mb-3">{error}</p>}
			<form onSubmit={handleSubmit} className="space-y-4">
				<div>
					<label className="block text-sm font-medium text-gray-700 mb-1">
						{t("auth.signUp.name")}
						<input
							name="name"
							type="text"
							value={name}
							onChange={(e) => setName(e.target.value)}
							required
							className="mt-1 block w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
						/>
					</label>
				</div>
				<div>
					<label className="block text-sm font-medium text-gray-700 mb-1">
						{t("auth.signUp.email")}
						<input
							name="email"
							type="email"
							value={email}
							onChange={(e) => setEmail(e.target.value)}
							required
							className="mt-1 block w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
						/>
					</label>
				</div>
				<div>
					<label className="block text-sm font-medium text-gray-700 mb-1">
						{t("auth.signUp.password")}
						<input
							name="password"
							type="password"
							value={password}
							onChange={(e) => setPassword(e.target.value)}
							required
							minLength={8}
							maxLength={128}
							className="mt-1 block w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
						/>
						<p className="mt-1 text-xs text-gray-500">
							{t("auth.signUp.passwordHint")}
						</p>
					</label>
				</div>
				<div className="flex items-start gap-2">
					<input
						id="tos"
						type="checkbox"
						checked={tosAccepted}
						onChange={(e) => setTosAccepted(e.target.checked)}
						className="mt-0.5 h-4 w-4 rounded border-gray-300"
					/>
					<label htmlFor="tos" className="text-sm text-gray-700">
						{t("auth.signUp.tosAgree")}{" "}
						<a
							href="/terms"
							target="_blank"
							rel="noopener noreferrer"
							className="text-blue-600 hover:underline"
						>
							{t("auth.signUp.termsOfService")}
						</a>
					</label>
				</div>
				<button
					type="submit"
					disabled={loading || !tosAccepted}
					className="w-full rounded bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
				>
					{loading ? t("auth.signUp.submitting") : t("auth.signUp.submit")}
				</button>
			</form>
			<PlatformSocialLoginButtons onError={setError} redirect={redirect} />
			<p className="mt-3 text-center text-xs text-gray-500">
				{t("auth.signUp.privacyNotice")}{" "}
				<a
					href="/privacy"
					target="_blank"
					rel="noopener noreferrer"
					className="text-blue-600 hover:underline"
				>
					{t("auth.signUp.privacyPolicy")}
				</a>
			</p>
			<p className="mt-4 text-sm text-gray-600">
				{t("auth.signUp.hasAccount")}{" "}
				<Link to={signInLink} className="text-blue-600 hover:underline">
					{t("auth.signUp.signInLink")}
				</Link>
			</p>
		</>
	);
}
